import { Component, computed, input, signal, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwBadgeComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  DialogFormControls,
  DialogOptions,
  SearchOption,
  SingleSelectOption,
  TableActionMenu,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { AwDropdownFormComponent } from '../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { EmployeeService } from '../../../../core/services/employee.service';
import { ThemeService } from '../../../../core/services/theme.service';
import { CommonModule } from '@angular/common';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import {
  Organization,
  UserService,
} from '../../../../core/services/user.service';
import { IntegrationService } from '../../../../core/services/integration.service';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import { UpdateEmployee$Params } from '../../../../api/fn/employee-management/update-employee';
import { Integration, ParentIntegration } from '../../../../api/models';

@Component({
  selector: 'app-employee-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AwTableComponent,
    AwDividerComponent,
    AwSearchComponent,
    AwButtonDirective,
    AwActionBarComponent,
    AwDropdownFormComponent,
    AwDialogComponent,
    AwSpinnerComponent,
    AwToastComponent,
    ReactiveFormsModule,
    MatIcon,
  ],
  templateUrl: './employee-management.component.html',
  styleUrls: [
    './employee-management.component.scss',
    '../../../../../styles/management.scss',
  ],
})
export class EmployeeManagementComponent {
  constructor(
    private router: Router,
    private employeeService: EmployeeService,
    private integrationService: IntegrationService,
    private parentIntegrationService: ParentIntegrationService,
    private userService: UserService,
    private themeService: ThemeService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  container_theme = computed(() => this.themeService.themeSignal());

  employeeOrganization: Organization | null = null;
  employeeOrganizationId: string = '';

  userPermissions: Record<string, boolean> = {};

  openChangeStatusMultipleDialog: boolean = false;
  openDeleteEmployeeDialog: boolean = false;
  openDeleteMultipleEmployeesDialog: boolean = false;
  openChangeStatusSingleDialog: boolean = false;

  disableChangeStatus: boolean = true;
  disableDeleteEmployee: boolean = true;

  employeeToDelete: TableRow | null = null;
  employeeToUpdate: TableRow | null = null;

  checkedRows: TableRow[] = [];
  isLoading: boolean = false;
  employeeStatus: string | undefined = '';
  searchInputValue!: string;
  selectionOptions: SearchOption[] = [];
  title = input<string>('Expiry Date Range');

  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];

  enableStickyOptions = signal(true);
  sortDisabled = signal(false);

  statusOptions = signal<SingleSelectOption[]>([
    {
      label: 'Active',
    },
    {
      label: 'Disabled',
    },
  ]);

  formFieldsData = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Employee(s)`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  dialogOptionsStatus: DialogOptions = {
    variant: 'standard',
    title: 'Change Status',
    description: `Change the status of the selected employee(s).`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  optionsMenu: TableActionMenu[] = [];

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'employeeInfo',
      label: 'User Name',
      combineFields: [
        'employeeInfo.employeeName',
        'employeeInfo.detailsPageRoute',
        'employeeInfo.employeeId',
      ],
      combineTemplate: (values: any[]) => ({
        template: this.CustomTemplate(values),
      }),
    },
    {
      type: TableCellTypes.Title,
      key: 'organizationInfo.organizationName',
      label: 'Integration Partner',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'dates.modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'dates.date_added',
      label: 'Date Added',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];

  CustomTemplate = (values: any) => `
  <div class="custom-template">
    <a href="${values[1]}">${values[0]}</a>
  </div>`;

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Add Employee',
        buttonType: 'outlined',
        action: () =>
          this.router.navigate(['/employee-management/add-employee']),
      },
    },
  ]);

  private getOrganizationWithFallback(organizationId: string): Observable<Integration | ParentIntegration | null> {
    return this.integrationService.getIntegrationById({ organizationId }).pipe(
      map(org => org ?? null),
      catchError((err) =>
        err.status === 404
          ? this.parentIntegrationService.getParentIntegrationById({ organizationId }).pipe(
              map(org => org ?? null),
              catchError(() => of(null))
            )
          : of(null)
      )
    );
  }

  ngOnInit() {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();
    if (this.userPermissions['canDelete'] || this.userPermissions['canEdit']) {
      this.columnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.userPermissions['canEdit']) {
      this.optionsMenu.push({
        title: 'Change Status',
        action: (row: { rowData: any; actionName: string }) => {
          this.openChangeStatusSingleDialog = true;
          this.employeeToUpdate = row.rowData;
        },
      });
    }
    if (this.userPermissions['canDelete']) {
      this.optionsMenu.push({
        title: 'Delete',
        action: (row: { rowData: any; actionName: string }) => {
          this.openDeleteEmployeeDialog = true;
          this.employeeToDelete = row.rowData;
        },
      });
    }

    this.employeeOrganization = this.userService.getOrganization();
    this.employeeOrganizationId =
      this.userService.getUserInfo()?.organizationId || '';

    // AssetWorks Employees get read/write access to ALL employees of ALL Integration Partners (including AssetWorks)
    if (this.employeeOrganization === 'assetworks-employee') {
      this.employeeService
        .getAllEmployees()
        .pipe(
          switchMap((employees) => {
            if (!employees || employees.length === 0) {
              return of({ employees: [], organizationMap: new Map() });
            }

            const uniqueOrgIds = [...new Set(employees.map(e => e.organizationId).filter(id => id))];
            if (uniqueOrgIds.length === 0) {
              return of({ employees, organizationMap: new Map() });
            }

            const orgRequests = uniqueOrgIds.map(orgId =>
              this.getOrganizationWithFallback(orgId!).pipe(
                map(org => ({ orgId, org }))
              )
            );

            return forkJoin(orgRequests).pipe(
              map(results => {
                const organizationMap = new Map(results.map(r => [r.orgId, r.org]));
                return { employees, organizationMap };
              })
            );
          }),
          finalize(() => (this.isLoading = false)),
          catchError((error) => {
            this.buildToastMessage('Failed to fetch employee data', 'error');
            return of({ employees: [], organizationMap: new Map() });
          })
        )
        .subscribe(({ employees, organizationMap }) => {
          const employeeData: any[] = employees.map(employee => {
            const organization = organizationMap.get(employee.organizationId!);
            return {
              employeeInfo: {
                employeeName: employee.name,
                employeeEmail: employee.emailAddress,
                employeeId: employee.id,
                detailsPageRoute: `/employee-management/employee-details/edit?employeeName=${employee.name}&employeeEmail=${employee.emailAddress}&employeeId=${employee.id}&organizationId=${employee.organizationId}`,
              },
              organizationInfo: {
                organizationId: employee.organizationId,
                organizationName: (organization as any)?.organizationName || (organization as any)?.name,
              },
              badgeNested: {
                enabled: employee.enabled,
                color: employee.enabled
                  ? 'success-green-700'
                  : 'aw-dark-gray-1000',
                label: employee.enabled ? 'Active' : 'Disabled',
              },
              dates: {
                modified_date: employee.modifiedOn
                  ? new Date(employee.modifiedOn).toLocaleDateString()
                  : 'N/A',
                date_added: employee.createdOn
                  ? new Date(employee.createdOn).toLocaleDateString()
                  : 'N/A',
                logon_date: employee.lastLogon
                  ? new Date(employee.lastLogon).toLocaleDateString()
                  : 'N/A',
              },
            };
          });

          this.tableData.set(employeeData);
          this.tableDataCopy = employeeData;
        });
    }
    // Integration Partner Employees are limited to read/write access for employees within their organization only
    else if (this.employeeOrganization === 'integration-employee') {
      forkJoin({
        organizationInfo: this.getOrganizationWithFallback(this.employeeOrganizationId),
        employees: this.employeeService.getEmployeesByOrganization({
          organizationId: this.employeeOrganizationId,
        }).pipe(catchError(() => of([]))),
      })
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(({ organizationInfo, employees }) => {
          const employeeData: any[] = (employees || []).map(employee => ({
            employeeInfo: {
              employeeName: employee.name,
              employeeEmail: employee.emailAddress,
              employeeId: employee.id,
              detailsPageRoute: `/employee-management/employee-details/${employee.id}`,
            },
            organizationInfo: {
              organizationId: employee.organizationId,
              organizationName: (organizationInfo as any)?.organizationName || (organizationInfo as any)?.name,
            },
            badgeNested: {
              enabled: employee.enabled,
              color: employee.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: employee.enabled ? 'Active' : 'Disabled',
            },
            dates: {
              modified_date: employee.modifiedOn
                ? new Date(employee.modifiedOn).toLocaleDateString()
                : 'N/A',
              date_added: employee.createdOn
                ? new Date(employee.createdOn).toLocaleDateString()
                : 'N/A',
              logon_date: employee.lastLogon
                ? new Date(employee.lastLogon).toLocaleDateString()
                : 'N/A',
            },
          }));

          this.tableData.set(employeeData);
          this.tableDataCopy = employeeData;
        });
    }
  }

  onSearchInput(searchInput: string) {
    this.awTableComponent.applyFilter(searchInput);
  }

  // How to accept multiple filters?
  onFilterSelected(status: SingleSelectOption | null) {
    if (status === null) {
      this.awTableComponent.applyFilter('');
      return;
    }
    this.awTableComponent.applyFilter(status!.label);
  }

  onChangeStatusSingle(employeeToUpdate: TableRow, newStatus: boolean) {
    this.isLoading = true;

    const organizationInfo = employeeToUpdate['organizationInfo'];
    const employeeInfo = employeeToUpdate['employeeInfo'];
    const employeeParams: UpdateEmployee$Params = {
      organizationId: organizationInfo.organizationId,
      employeeId: employeeInfo.employeeId,
      body: {
        emailAddress: employeeInfo.employeeEmail,
        enabled: newStatus,
        name: employeeInfo.employeeName,
      },
    };

    this.employeeService
      .updateEmployee(employeeParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeStatusSingleDialog = false;
        })
      )
      .subscribe((updated) => {
        if (updated) {
          const badge = employeeToUpdate['badgeNested'];
          badge.enabled = newStatus;
          badge.color = badge.enabled ? 'success-green-700' : 'aw-dark-gray-1000';
          badge.label = badge.enabled ? 'Active' : 'Disabled';

          this.buildToastMessage('Successfully updated employee', 'success');
        } else {
          this.buildToastMessage('Failed to update employee', 'error');
        }
      });
  }

  onChangeStatusMultiple(status: boolean) {
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }
    const employeesToChange = this.checkedRows.filter(
      (r) => r['badgeNested'].enabled !== status
    );
    if (!employeesToChange.length) {
      this.buildToastMessage(
        `No ${!status ? 'active' : 'disabled'} rows selected`,
        'alert'
      );
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = employeesToChange.map(
      (employee) => {
        const employeeInfo = employee['employeeInfo'];
        const organizationInfo = employee['organizationInfo'];
        const statusInfo = employee['badgeNested'];

        const employeeParams: UpdateEmployee$Params = {
          organizationId: organizationInfo.organizationId,
          employeeId: employeeInfo.employeeId,
          body: {
            emailAddress: employeeInfo.employeeEmail,
            enabled: !statusInfo.enabled,
            name: employeeInfo.employeeName,
          },
        };

        return this.employeeService
          .updateEmployee(employeeParams)
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeStatusMultipleDialog = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates: number = results.filter(
          (updated) => updated
        ).length;

        employeesToChange.forEach((employee, index) => {
          if (results[index]) {
            const badge = employee['badgeNested'];

            badge.enabled = status;
            badge.color = badge.enabled
              ? 'success-green-700'
              : 'aw-dark-gray-1000';
            badge.label = badge.enabled ? 'Active' : 'Disabled';
          }
        });

        if (numStatusUpdates === employeesToChange.length) {
          this.buildToastMessage(
            `Successfully ${
              status ? 'enabled' : 'disabled'
            } all selected employees`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `ALERT: ${numStatusUpdates} of ${
              employeesToChange.length
            } employees ${status ? 'enabled' : 'disabled'}`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            `Failed to ${status ? 'enable' : 'disable'} any selected employees`,
            'error'
          );
        }
      });
  }

  onDeleteEmployeeSingle(employeeId: string, organizationId: string) {
    this.isLoading = true;

    this.employeeService
      .deleteEmployee({
        organizationId: organizationId,
        employeeId: employeeId,
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openDeleteEmployeeDialog = false;
        })
      )
      .subscribe((employeeDeleted) => {
        if (employeeDeleted) {
          const filtered = this.tableDataCopy.filter(
            (row) => row['employeeInfo'].employeeId !== employeeId
          );
          this.tableData.set(filtered);
          this.tableDataCopy = [...filtered];
          this.buildToastMessage('Successfully deleted employee.', 'success');
        } else {
          this.buildToastMessage('Failed to deleted employee.', 'error');
        }
      });
  }

  onDeleteEmployeeMultiple() {
    const employeesToDelete = this.checkedRows;
    if (!employeesToDelete.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }

    this.isLoading = true;

    const deleteEmployeeRequests: Observable<boolean>[] = employeesToDelete.map(
      (employee) => {
        const employeeInfo = employee['employeeInfo'];
        const organizationInfo = employee['organizationInfo'];

        return this.employeeService.deleteEmployee({
          organizationId: organizationInfo.organizationId,
          employeeId: employeeInfo.employeeId,
        });
      }
    );

    forkJoin(deleteEmployeeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openDeleteMultipleEmployeesDialog = false;
        })
      )
      .subscribe((results) => {
        const numEmployeesDeleted: number = results.filter(
          (deleted) => deleted
        ).length;
        const totalEmployeesToDelete: number = employeesToDelete.length;

        const filtered = this.tableDataCopy.filter(
          (row) => !employeesToDelete.includes(row['employeeInfo'].employeeId)
        );
        this.tableData.set(filtered);
        this.tableDataCopy = [...filtered];

        if (numEmployeesDeleted === totalEmployeesToDelete) {
          this.buildToastMessage(
            'Successfully deleted all employees',
            'success'
          );
        } else if (numEmployeesDeleted > 0) {
          this.buildToastMessage(
            `ALERT: ${numEmployeesDeleted} of ${totalEmployeesToDelete} employees deleted`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            'Failed to delete any selected employees',
            'error'
          );
        }
      });
  }

  dialogFormValuesHandler(formValues: any, key: string) {
    if (key === 'status-multiple') {
      if (formValues && formValues.valid) {
        this.onChangeStatusMultiple(formValues.formData.changeStatus.value);
      }
    } else if (key === 'status-single') {
      if (this.employeeToUpdate && formValues && formValues.valid) {
        this.onChangeStatusSingle(this.employeeToUpdate, formValues.formData.changeStatus.value);
      }
    } else if (key === 'delete-multiple') {
      if (this.checkedRows.length) {
        this.onDeleteEmployeeMultiple();
      }
    } else if (key === 'delete-single') {
      if (this.employeeToDelete) {
        this.onDeleteEmployeeSingle(
          this.employeeToDelete['employeeInfo'].employeeId,
          this.employeeToDelete?.['organizationInfo'].organizationId
        );
      }
    }
  }

  dialogCloseHandler(key: string) {
    if (key === 'status-multiple') {
      this.openChangeStatusMultipleDialog = false;
    } else if (key === 'status-single') {
      this.openChangeStatusSingleDialog = false;
    } else if (key === 'delete-single') {
      this.openDeleteEmployeeDialog = false;
    } else if (key === 'delete-multiple') {
      this.openDeleteMultipleEmployeesDialog = false;
    }
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    if (this.checkedRows.length > 0) {
      this.disableChangeStatus = false;
      this.disableDeleteEmployee = false;
    } else {
      this.disableChangeStatus = true;
      this.disableDeleteEmployee = true;
    }
  }

  handleActionMenu(row: any) {}
}
